import { ref } from 'vue';
import { remoteConfig, getValue, safeFetchAndActivate, isQuotaExceededError } from '@/firebase';

type FeatureFlagsMap = Record<string, boolean>;
type TVConfig = {
  featureFlags?: Record<string, unknown>;
};

const featureFlags = ref<FeatureFlagsMap>({});
let isInitialized = false;

function parseFeatureFlags(raw: string): FeatureFlagsMap {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as TVConfig;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    if (!parsed.featureFlags || typeof parsed.featureFlags !== 'object') {
      return {};
    }

    const flags: FeatureFlagsMap = {};
    Object.entries(parsed.featureFlags).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        flags[key] = value;
      }
    });

    return flags;
  } catch (error) {
    console.warn('[FeatureFlags] Failed to parse TV_config JSON:', error);
    return {};
  }
}

export async function fetchFeatureFlags(): Promise<void> {
  if (isInitialized) return;

  try {
    const success = await safeFetchAndActivate();
    if (!success) {
      console.warn('[FeatureFlags] Failed to fetch remote config (quota exceeded or error). Using defaults.');
    }

    const tvConfigValue = getValue(remoteConfig, 'TV_config');
    const tvConfigJson = tvConfigValue.asString();
    featureFlags.value = parseFeatureFlags(tvConfigJson);
    isInitialized = true;
  } catch (error: any) {
    if (isQuotaExceededError(error)) {
      console.error('[FeatureFlags] Quota exceeded error while fetching feature flags. Using defaults.');
    } else {
      console.error('[FeatureFlags] Error fetching feature flags:', error);
    }
    featureFlags.value = {};
    isInitialized = true;
  }
}

export function useFeatureFlags() {
  const isFlagEnabled = (flagName: string, defaultValue = false) => {
    const value = featureFlags.value[flagName];
    return typeof value === 'boolean' ? value : defaultValue;
  };

  return {
    featureFlags,
    fetchFeatureFlags,
    isFlagEnabled,
  };
}
